import { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { clearMedia } from '../../../store/mediaSlice';
import { extractVars, validateSampleValue } from '../TemplateUtils';
import { validateRegularTemplateForm, getVarValidationErrors, validateVariableSamples } from '../utils/templateValidation';

export const useRegularTemplate = (formData, setFormData, setValidationErrors) => {
  const dispatch = useDispatch();
  const sampleValuesRef = useRef({});
  const sampleErrorsRef = useRef({});
  const [sampleValues, setSampleValues] = useState({});
  const [sampleErrors, setSampleErrors] = useState({});
  const [sampleValidationMessage, setSampleValidationMessage] = useState('');
  const [localHeaderError, setLocalHeaderError] = useState('');
  const [localBodyError, setLocalBodyError] = useState('');

  const headerVars = useMemo(() => extractVars(formData.headerText), [formData.headerText]);
  const bodyVars = useMemo(() => extractVars(formData.bodyText), [formData.bodyText]);

  useEffect(() => {
    const allVars = Array.from(new Set([...(headerVars || []), ...(bodyVars || [])]));
    const newVals = {};
    const newErrs = {};

    allVars.forEach((v) => {
      newVals[v] = sampleValuesRef.current?.[v] || '';
      if (sampleErrorsRef.current?.[v]) newErrs[v] = sampleErrorsRef.current[v];
    });

    sampleValuesRef.current = newVals;
    setSampleValues(newVals);
    sampleErrorsRef.current = newErrs;
  }, [headerVars, bodyVars]);

  useEffect(() => {
    const errors = getVarValidationErrors(headerVars, bodyVars);
    setLocalHeaderError(errors.header || '');
    setLocalBodyError(errors.body || '');
  }, [headerVars, bodyVars]);

  const handleSampleChange = useCallback((name, value) => {
    if (sampleValidationMessage) setSampleValidationMessage('');
    sampleValuesRef.current = { ...sampleValuesRef.current, [name]: value };
    setSampleValues((prev) => ({ ...prev, [name]: value }));
    const err = validateSampleValue(value);
    sampleErrorsRef.current = { ...sampleErrorsRef.current, [name]: err };
    setSampleErrors({ ...sampleErrorsRef.current });
  }, [sampleValidationMessage]);

  const validateForm = () => {
    const errors = validateRegularTemplateForm(formData);
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateSamples = () => {
    const result = validateVariableSamples({
      headerText: formData.headerText,
      bodyText: formData.bodyText,
      sampleValues: sampleValuesRef.current,
      sampleErrors: sampleErrorsRef.current
    });

    if (!result.valid) {
      const newErrs = {};
      result.missing?.forEach((m) => { newErrs[m] = 'Sample is required'; });
      sampleErrorsRef.current = newErrs;
      setSampleErrors(newErrs);
      setSampleValidationMessage(result.message);
    }

    return result.valid;
  };

  const handleHeaderTextChange = (value) => {
    setFormData('headerText', value);
    const hasHeader = String(value || '').trim() !== '';
    const newHeaderType = hasHeader ? 'text' : 'none';
    if (formData.headerType !== newHeaderType) {
      setFormData('headerType', newHeaderType);
      if (newHeaderType === 'text') dispatch(clearMedia());
    }
  };

  return {
    headerVars,
    bodyVars,
    sampleValues,
    sampleErrors,
    handleSampleChange,
    sampleValidationMessage,
    setSampleValidationMessage,
    localHeaderError,
    localBodyError,
    validateForm,
    validateSamples,
    handleHeaderTextChange,
    sampleValuesRef,
  };
};
